import React, { useMemo } from "react";
import { useQuery } from "wasp/client/operations";
import { getVisibleSections } from "wasp/client/operations";
import CommonLayout from "../../../components/layout/CommonLayout";
import { dashboardLayoutConfig } from "./dashboardConfig";
import { useViewMode } from "../../contexts/ViewModeContext";
import { useLocationContext } from "../../contexts/LocationContext";

interface DashboardLayoutProps {
  children: React.ReactNode;
  showViewSwitcher?: boolean;
  activeView?: "cards" | "list" | "table";
  onViewChange?: (view: "cards" | "list" | "table") => void;
  forceSidebarCollapse?: boolean;
  customTopBarContent?: React.ReactNode;
}

export default function DashboardLayout({
  children,
  showViewSwitcher = false,
  activeView = "cards",
  onViewChange,
  forceSidebarCollapse = false,
  customTopBarContent,
}: DashboardLayoutProps) {
  const { isStudentLife, isAcademics, isCampus, isMarketplace } = useViewMode();
  const { selectedLocation } = useLocationContext();
  const { data } = useQuery(getVisibleSections, {
    locationId: selectedLocation?.id ?? null,
  });
  const visibilityData = data as
    | { visibleByKey?: Record<string, boolean> }
    | undefined;
  const visibleByKey = visibilityData?.visibleByKey ?? {};

  const modifiedConfig = useMemo(() => {
    const filteredSections = dashboardLayoutConfig.menuSections
      .filter((section) => {
        // Academics View
        if (
          section.label === "Academic Resources" ||
          section.label === "Academics"
        )
          return isAcademics;

        // Student View
        if (
          [
            "Student",
            "Support & Safety",
            "Community",
            "Student Exchange",
          ].includes(section.label)
        )
          return isStudentLife;

        // Campus Life View
        if (["Campus", "Campus Life"].includes(section.label)) return isCampus;

        // Marketplace View
        if (section.label === "Marketplace") return isMarketplace;

        // Always show empty label sections (like Information/About)
        if (section.label === "") return true;

        return false;
      })
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (!item.sectionKey) return true;
          return visibleByKey[item.sectionKey] !== false;
        }),
      }))
      .filter((section) => section.items.length > 0);
    return {
      ...dashboardLayoutConfig,
      menuSections: filteredSections,
      customTopBarContent,
    };
  }, [isStudentLife, isAcademics, isCampus, isMarketplace, visibleByKey, customTopBarContent]);

  return (
    <CommonLayout
      config={modifiedConfig}
      showViewSwitcher={showViewSwitcher}
      activeView={activeView}
      onViewChange={onViewChange}
      forceSidebarCollapse={forceSidebarCollapse}
    >
      {children}
    </CommonLayout>
  );
}
